import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CurrencyRupeeOutlined, SettingsOutlined } from "@mui/icons-material";
import { createPortal } from "react-dom";
import { searchCustomers } from "../../api/customerApi";
import "./paymentRecord.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import backgroundPattern from "../../assets/pasbookpng.png";

const PAYMENT_MODES = [
    "Bank Remittance",
    "Bank Transfer",
    "Cash",
    "Cheque",
    "Credit Card",
    "UPI"
];

const DEPOSIT_ACCOUNTS = [
    {
        group: "Cash & Bank",
        items: ["Cash", "Petty Cash", "Undeposited Funds"]
    },
    {
        group: "Other Current Liability",
        items: [
            "Employee Reimbursements",
            "Opening Balance Adjustments",
            "TDS Payable"
        ]
    }
];


function safeArray(value) {
    return Array.isArray(value) ? value : [];
}

function CustomSelect({ value, onChange, options, placeholder = "Select..." }) {
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState(null);

    useEffect(() => {
        if (!open || !triggerRef.current) return undefined;
        const update = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
            const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
            const below = window.innerHeight - rect.bottom - 12;
            const above = rect.top - 12;
            const showAbove = below < 260 && above > below;
            const maxHeight = Math.max(160, Math.min(300, showAbove ? above : below));
            setStyle({ position: "fixed", left, top: showAbove ? rect.top - maxHeight - 6 : rect.bottom + 6, width, maxHeight, zIndex: 10050 });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    useEffect(() => {
        const close = (event) => {
            if (triggerRef.current && !triggerRef.current.contains(event.target) && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const list = open && style ? (
        <div className="payment-customer-dropdown" style={style} ref={dropdownRef}>
            {options.map((option) => (
                <button
                    type="button"
                    key={option.value}
                    className="payment-customer-option"
                    onClick={() => {
                        onChange(option.value);
                        setOpen(false);
                    }}
                >
                    <strong>{option.label}</strong>
                </button>
            ))}
        </div>
    ) : null;

    return (
        <>
            <div className="payment-customer-picker" ref={triggerRef}>
                <button
                    type="button"
                    className={`payment-field payment-customer-trigger ${value ? "has-value" : ""}`}
                    onClick={() => setOpen((current) => !current)}
                >
                    <span>{value || placeholder}</span>
                    <span className="payment-chevron" aria-hidden="true" />
                </button>
            </div>
            {typeof document !== "undefined" && createPortal(list, document.body)}
        </>
    );
}

function CustomerPicker({ value, onChange }) {
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [style, setStyle] = useState(null);
    const [localCustomers, setLocalCustomers] = useState([]);

    const customerName = (customer) =>
        customer?.name || customer?.displayName || customer?.customer_name || customer?.customerName || "";

    useEffect(() => {
        try {
            setLocalCustomers(safeArray(JSON.parse(localStorage.getItem("customers") || "[]")));
        } catch {
            setLocalCustomers([]);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            try {
                const response = await searchCustomers({ name: query.trim() });
                const apiRows = Array.isArray(response?.data)
                    ? response.data
                    : safeArray(response?.data?.data || response?.data?.customers);
                const q = query.trim().toLowerCase();
                const merged = [...apiRows, ...localCustomers].filter((row) =>
                    customerName(row).toLowerCase().includes(q)
                );
                const seen = new Set();
                setCustomers(merged.filter((row) => {
                    const key = String(row.id ?? customerName(row)).toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return Boolean(customerName(row));
                }));
            } catch {
                const q = query.trim().toLowerCase();
                setCustomers(localCustomers.filter((row) => customerName(row).toLowerCase().includes(q)));
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => window.clearTimeout(timer);
    }, [open, query, localCustomers]);

    useEffect(() => {
        if (!open || !triggerRef.current) return undefined;
        const update = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
            const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
            const below = window.innerHeight - rect.bottom - 12;
            const above = rect.top - 12;
            const showAbove = below < 260 && above > below;
            const maxHeight = Math.max(160, Math.min(300, showAbove ? above : below));
            setStyle({ position: "fixed", left, top: showAbove ? rect.top - maxHeight - 6 : rect.bottom + 6, width, maxHeight, zIndex: 10050 });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    useEffect(() => {
        const close = (event) => {
            if (triggerRef.current && !triggerRef.current.contains(event.target) && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const list = open && style ? (
        <div className="payment-customer-dropdown" style={style} ref={dropdownRef}>
            <div className="payment-customer-search">
                <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customer..." />
            </div>
            {loading && <div className="payment-dropdown-state">Searching...</div>}
            {!loading && customers.length === 0 && <div className="payment-dropdown-state">No customers found.</div>}
            {!loading && customers.map((customer, index) => (
                <button type="button" className="payment-customer-option" key={customer.id ?? `${customerName(customer)}-${index}`} onClick={() => {
                    onChange(customerName(customer), customer);
                    setOpen(false);
                    setQuery("");
                }}>
                    <strong>{customerName(customer)}</strong>
                    <span>{customer.contactNumber || customer.phone || customer.email || "Customer"}</span>
                </button>
            ))}
            <button type="button" className="payment-add-customer" onClick={() => {
                setOpen(false);
                window.location.assign("/dashboard/customers/new");
            }}>+ Add New Customer</button>
        </div>
    ) : null;

    return (
        <>
            <div className="payment-customer-picker" ref={triggerRef}>
                <button type="button" className={`payment-field payment-customer-trigger ${value ? "has-value" : ""}`} onClick={() => setOpen((current) => !current)}>
                    <span>{value || "Select or add a customer"}</span>
                    <span className="payment-chevron" aria-hidden="true" />
                </button>
            </div>
            {typeof document !== "undefined" && createPortal(list, document.body)}
        </>
    );
}

export default function PaymentRecord({
    onAddPayment,
    initialData,
    isModal = false,
    onCancel,
    onSaveModal
}) {
    const navigate = useNavigate();

    const isEditing = Boolean(initialData);


    const [formData, setFormData] = useState(
        initialData || {
            customerName: "",
            amountReceived: "",
            bankCharges: "",
            paymentDate: new Date().toISOString().slice(0, 10),
            paymentNumber: "1",
            paymentMode: "Cash",
            depositTo: "Petty Cash",
            referenceNumber: "",
            taxDeducted: "no",
            notes: "",
            sendThankYou: true,
            emailCustomer: true,
            customerEmail: ""
        }
    );


    const [attachments, setAttachments] =
        useState([]);

    // =====================================================
    // INPUT CHANGE
    // =====================================================

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // =====================================================
    // DROPDOWN
    // =====================================================


    // =====================================================
    // SAVE / UPDATE PAYMENT
    // =====================================================

    const handleSaveAsPaid = () => {
        // Validate amount
        if (!formData.amountReceived) {
            toast.warning(
                "Please enter Amount Received."
            );
            return;
        }

        if (
            Number(formData.amountReceived) <= 0
        ) {
            toast.error(
                "Amount received must be greater than 0."
            );
            return;
        }

        // =================================================
        // EDIT EXISTING PAYMENT
        // =================================================

        if (isEditing) {
            const updatedPayment = {
                ...formData,
                id: initialData.id
            };

            if (onSaveModal) {
                onSaveModal(updatedPayment);
            }

            toast.success(
                "Payment updated successfully."
            );

            return;
        }

        // =================================================
        // CREATE NEW PAYMENT
        // =================================================

        const newEntry = {
            ...formData,
            id: Date.now()
        };

        if (onAddPayment) {
            onAddPayment(newEntry);
        }

        toast.success(
            "Amount received successfully."
        );

        setTimeout(() => {
            navigate("/dashboard/payments");
        }, 1000);
    };

    // =====================================================
    // FILE UPLOAD
    // =====================================================

    const handleFileUpload = (e) => {
        const files = Array.from(
            e.target.files || []
        );

        if (files.length > 5) {
            toast.warning(
                "Maximum 5 files allowed."
            );
            return;
        }

        if (
            files.some(
                (file) =>
                    file.size >
                    5 * 1024 * 1024
            )
        ) {
            toast.error(
                "Each file must be 5MB or less."
            );
            return;
        }

        setAttachments(files);
    };

    // =====================================================
    // RETURN
    // =====================================================

    return (
        <div className={`payment-page ${isModal ? "payment-page-modal" : ""}`}>
            {!isModal && (
                <div
                    className="payment-background-watermark"
                    style={{ backgroundImage: `url(${backgroundPattern})` }}
                />
            )}

            {!isModal && (
                <header className="payment-header">
                    <div className="payment-header-left">
                        <div className="payment-page-icon" aria-hidden="true"><CurrencyRupeeOutlined /></div>
                        <div className="payment-page-title-wrap">
                            <h1>{isEditing ? "Edit Payment" : "Record Payment"}</h1>
                            <p>Record and manage customer payments received by your business.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="payment-header-back"
                        onClick={() => {
                            if (onCancel) {
                                onCancel();
                            } else {
                                // When used as a standalone route, navigate back
                                navigate("/dashboard/payments");
                            }
                        }}
                        aria-label="Back to payments"
                    >
                        ←
                    </button>
                </header>
            )}

            <main className="payment-shell">
                <div
                    className={`payment-container ${
                        isModal ? "modal-container" : ""
                    }`}
                >

            {/* =================================================
                TOAST
            ================================================= */}

            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="form-header">

                <h2>
                    {isEditing
                        ? "Edit Payment"
                        : "Record Payment"}
                </h2>

                <button
                    className="close-btn"
                    type="button"
                    onClick={() => {
                        if (isEditing && onCancel) {
                            onCancel();
                        } else {
                            navigate(
                                "/dashboard/payments"
                            );
                        }
                    }}
                >
                    &times;
                </button>

            </div>

            {/* =================================================
                FORM CONTENT
            ================================================= */}

            <div className="form-content">

                <div className="payment-section-head">
                    <div>
                        <h2>Payment details</h2>
                        <p>Enter the customer and payment information.</p>
                    </div>
                    <span className="payment-required">* Required</span>
                </div>

                {/* =================================================
                    CUSTOMER
                ================================================= */}

                <div className="form-row align-start">

                    <label className="required-label">
                        Customer Name*
                    </label>

                    <div className="input-group">

                        <div className="customer-select-wrapper">
                            <CustomerPicker
                                value={formData.customerName}
                                onChange={(name, row) => {
                                    handleInputChange("customerName", name);
                                    handleInputChange("customerEmail", row?.email || "");
                                }}
                            />
                            <button type="button" className="customer-details-btn" onClick={() => navigate("/dashboard/customers/new")}>
                                Create Customer <span aria-hidden="true">›</span>
                            </button>
                        </div>

                        <div className="pan-link">
                            PAN:{" "}
                            <a href="#pan">
                                Add PAN
                            </a>
                        </div>

                    </div>
                </div>

                {/* =================================================
                    AMOUNT RECEIVED
                ================================================= */}

                <div className="form-row">

                    <label className="required-label">
                        Amount Received*
                    </label>

                    <div className="input-group">

                        <div className="currency-input-wrapper">

                            <span className="currency-prefix" aria-hidden="true"><span className="currency-symbol">₹</span></span>

                            <input
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={String(formData.amountReceived ?? "").replace(/[^0-9.]/g, "")}
                                onChange={(e) => {
                                    const value = e.target.value
                                        .replace(/[^0-9.]/g, "")
                                        .replace(/(\..*)\./g, "$1")
                                        .replace(/^(\d*\.\d{0,2}).*$/, "$1");

                                    handleInputChange("amountReceived", value);
                                }}
                                onBlur={() => {
                                    const value = String(formData.amountReceived ?? "").replace(/[^0-9.]/g, "");

                                    if (!value) {
                                        toast.warning("Please enter the amount received.");
                                    } else if (Number(value) <= 0) {
                                        toast.error("Amount received must be greater than 0.");
                                    } else {
                                        handleInputChange("amountReceived", value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (["-", "+", "e", "E"].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder="0.00"
                                aria-label="Amount Received"
                            />

                        </div>

                    </div>
                </div>

                {/* =================================================
                    BANK CHARGES
                ================================================= */}

                <div className="form-row">

                    <label>
                        Bank Charges (if any)
                    </label>

                    <div className="input-group">

                        <input
                            type="number"
                            value={
                                formData.bankCharges
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "bankCharges",
                                    e.target.value
                                )
                            }
                        />

                    </div>
                </div>

                {/* =================================================
                    PAYMENT DATE
                ================================================= */}

                <div className="form-row">

                    <label className="required-label">
                        Payment Date*
                    </label>

                    <div className="input-group">

                        <input
                            type="date"
                            value={
                                formData.paymentDate
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "paymentDate",
                                    e.target.value
                                )
                            }
                        />

                    </div>
                </div>

                {/* =================================================
                    PAYMENT NUMBER
                ================================================= */}

                <div className="form-row">

                    <label className="required-label">
                        Payment #*
                    </label>

                    <div className="input-group flex-row">

                        <input
                            type="text"
                            value={
                                formData.paymentNumber
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "paymentNumber",
                                    e.target.value
                                )
                            }
                        />

                        <button
                            type="button"
                            className="icon-btn settings-btn"
                            title="Configure"
                        >
                            <SettingsOutlined />
                        </button>

                    </div>
                </div>

                {/* =================================================
                    PAYMENT MODE
                ================================================= */}

                <div className="form-row">
                    <label>Payment Mode</label>
                    <div className="input-group">
                        <CustomSelect
                            value={formData.paymentMode}
                            onChange={(value) => handleInputChange("paymentMode", value)}
                            options={PAYMENT_MODES.map((mode) => ({ value: mode, label: mode }))}
                            placeholder="Select payment mode"
                        />
                    </div>
                </div>

                {/* =================================================
                    DEPOSIT TO
                ================================================= */}

                <div className="form-row">
                    <label className="required-label">Deposit To*</label>
                    <div className="input-group">
                        <CustomSelect
                            value={formData.depositTo}
                            onChange={(value) => handleInputChange("depositTo", value)}
                            options={DEPOSIT_ACCOUNTS.flatMap((group) =>
                                group.items.map((account) => ({ value: account, label: account }))
                            )}
                            placeholder="Select deposit account"
                        />
                    </div>
                </div>

                {/* =================================================
                    REFERENCE
                ================================================= */}

                <div className="form-row">

                    <label>
                        Reference#
                    </label>

                    <div className="input-group">

                        <input
                            type="text"
                            value={
                                formData.referenceNumber
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "referenceNumber",
                                    e.target.value
                                )
                            }
                        />

                    </div>
                </div>

                {/* =================================================
                    TAX DEDUCTED
                ================================================= */}

                <div className="form-row">

                    <label>
                        Tax deducted?
                    </label>

                    <div className="radio-group">

                        <label className="radio-label">

                            <input
                                type="radio"
                                name="taxDeducted"
                                value="no"
                                checked={
                                    formData.taxDeducted ===
                                    "no"
                                }
                                onChange={(e) =>
                                    handleInputChange(
                                        "taxDeducted",
                                        e.target.value
                                    )
                                }
                            />

                            <span>
                                No Tax deducted
                            </span>

                        </label>

                        <label className="radio-label">

                            <input
                                type="radio"
                                name="taxDeducted"
                                value="yes"
                                checked={
                                    formData.taxDeducted ===
                                    "yes"
                                }
                                onChange={(e) =>
                                    handleInputChange(
                                        "taxDeducted",
                                        e.target.value
                                    )
                                }
                            />

                            <span>
                                Yes, TDS (Income Tax)
                            </span>

                        </label>

                    </div>
                </div>

                {/* =================================================
                    UNPAID INVOICES
                ================================================= */}

                <div className="table-section">

                    <div className="table-header">

                        <div className="table-title">

                            <h3>
                                Unpaid Invoices
                            </h3>

                            <button
                                type="button"
                                className="filter-btn"
                            >
                                📁 Filter by Date Range
                                ▾
                            </button>

                        </div>

                        <a
                            href="#clear"
                            className="clear-link"
                        >
                            Clear Applied Amount
                        </a>

                    </div>

                    <table className="invoices-table">

                        <thead>

                            <tr>
                                <th>DATE</th>
                                <th>
                                    INVOICE NUMBER
                                </th>
                                <th>
                                    INVOICE AMOUNT
                                </th>
                                <th>
                                    AMOUNT DUE
                                </th>
                                <th>
                                    PAYMENT RECEIVE
                                </th>
                                <th>
                                    PAYMENT
                                </th>
                            </tr>

                        </thead>

                        <tbody>

                            <tr>

                                <td
                                    colSpan="6"
                                    className="empty-row"
                                >
                                    There are no unpaid
                                    invoices associated
                                    with this customer.
                                </td>

                            </tr>

                        </tbody>

                    </table>

                    <div className="table-footer-note">
                        **List contains only SENT invoices
                    </div>

                </div>

                {/* =================================================
                    SUMMARY
                ================================================= */}

                <div className="summary-wrapper">

                    <div className="summary-card">

                        <div className="summary-line">

                            <span>
                                Amount Received :
                            </span>

                            <span>
                                {parseFloat(
                                    formData.amountReceived ||
                                        0
                                ).toFixed(2)}
                            </span>

                        </div>

                        <div className="summary-line">

                            <span>
                                Amount used for Payments :
                            </span>

                            <span>
                                0.00
                            </span>

                        </div>

                        <div className="summary-line">

                            <span>
                                Amount Refunded :
                            </span>

                            <span>
                                0.00
                            </span>

                        </div>

                        <div className="summary-line excess-line">

                            <span>

                                <span className="warning-icon">
                                    ⚠️
                                </span>{" "}
                                Amount in Excess:

                            </span>

                            <span className="excess-val">

                                ₹{" "}

                                {parseFloat(
                                    formData.amountReceived ||
                                        0
                                ).toFixed(2)}

                            </span>

                        </div>

                    </div>

                </div>

                {/* =================================================
                    NOTES
                ================================================= */}

                <div className="form-block">

                    <label>
                        Notes (Internal use. Not visible
                        to customer)
                    </label>

                    <textarea
                        rows="3"
                        value={
                            formData.notes
                        }
                        onChange={(e) =>
                            handleInputChange(
                                "notes",
                                e.target.value
                            )
                        }
                    />

                </div>

                {/* =================================================
                    FILE UPLOAD
                ================================================= */}

                <input
                    type="file"
                    id="fileUpload"
                    multiple
                    onChange={handleFileUpload}
                    style={{
                        display: "none"
                    }}
                />

                <label
                    htmlFor="fileUpload"
                    className="upload-btn"
                >
                    ⇧ Upload File ▾
                </label>

                {attachments.length > 0 && (
                    <div className="attachment-list">

                        {attachments.map(
                            (file, index) => (
                                <div key={index}>
                                    📎 {file.name}
                                </div>
                            )
                        )}

                    </div>
                )}

                {/* =================================================
                    THANK YOU CHECKBOX
                ================================================= */}

                <div className="checkbox-section">

                    <label className="custom-checkbox">

                        <input
                            type="checkbox"
                            checked={
                                formData.sendThankYou
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "sendThankYou",
                                    e.target.checked
                                )
                            }
                        />

                        <span>
                            Send a "Thank you" note for
                            this payment
                        </span>

                    </label>

                </div>

            </div>

            {/* =================================================
                EMAIL CUSTOMER CHECKBOX
            ================================================= */}

            {formData.sendThankYou && (
                <div className="indented-checkbox">

                    <label className="custom-checkbox">

                        <input
                            type="checkbox"
                            checked={
                                formData.emailCustomer
                            }
                            onChange={(e) =>
                                handleInputChange(
                                    "emailCustomer",
                                    e.target.checked
                                )
                            }
                        />

                        <span>
                            {formData.customerEmail ||
                                "Customer Email"}
                        </span>

                    </label>

                </div>
            )}

            {/* =================================================
                FOOTER ACTIONS
            ================================================= */}

            <div className="form-actions">

                {isEditing ? (
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                if (onCancel) {
                                    onCancel();
                                } else {
                                    navigate(
                                        "/dashboard/payments"
                                    );
                                }
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                                handleSaveAsPaid
                            }
                        >
                            Update Payment
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() =>
                                navigate(
                                    "/dashboard/payments"
                                )
                            }
                        >
                            Save as Draft
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                                handleSaveAsPaid
                            }
                        >
                            Save as Paid
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() =>
                                navigate(
                                    "/dashboard/payments"
                                )
                            }
                        >
                            Cancel
                        </button>
                    </>
                )}

            </div>

                </div>
            </main>
        </div>
    );
}